import React, { useEffect, useRef, useMemo } from "react";

import "chart.js/auto";
import { Chart, Line } from "react-chartjs-2";
import moment from "moment";
import "chartjs-adapter-moment";

//--------------------------------------------------------------------------------------------------------------
// Load airplane image once outside the plugin
const airplaneImage = new Image();
airplaneImage.src = "/Plane2Hx4.png"; // Updated to use Plan2.png

const airplanePlugin = {
  id: "airplane",
  afterDraw(chart, args, options) {
    const { ctx } = chart;
    const isLive = options.isLive;
    const flyAwayData = options.flyAwayData;

    let xPos, yPos, angle;

    if (isLive) {
      const meta = chart.getDatasetMeta(0);
      const dataPoints = meta?.data || [];

      if (dataPoints.length > 0) {
        // If flyAwayData is null, this is the start of a new game - use FIRST point
        // Otherwise, track the LAST point as the plane follows the line
        const targetPoint =
          !flyAwayData.current || flyAwayData.current.x === undefined
            ? dataPoints[0] // Start at first point (0,0 on graph)
            : dataPoints[dataPoints.length - 1]; // Follow last point

        if (
          !targetPoint ||
          targetPoint.x === undefined ||
          targetPoint.y === undefined
        )
          return;

        // Target position from chart data
        let targetX = targetPoint.x;
        let targetY = targetPoint.y;

        // Smooth position with LERP to reduce jitter (only after initial positioning)
        const posLerpFactor = 0.5; // Increased to keep airplane closer to the line
        if (flyAwayData.current && flyAwayData.current.x !== undefined) {
          xPos =
            flyAwayData.current.x +
            (targetX - flyAwayData.current.x) * posLerpFactor;
          yPos =
            flyAwayData.current.y +
            (targetY - flyAwayData.current.y) * posLerpFactor;
        } else {
          // First frame - position directly at start point
          xPos = targetX;
          yPos = targetY;
        }

        // Fixed angle for consistent upward trajectory
        angle = -0.35; // Angled more towards east while following the line (~10 degrees up)

        // Store last known position and angle for fly away
        flyAwayData.current = {
          x: xPos,
          y: yPos,
          angle: angle,
          startTime: null,
        };
      } else {
        // Don't render airplane until we have data
        return;
      }
    } else {
      // Fly away logic
      if (flyAwayData.current) {
        if (!flyAwayData.current.startTime) {
          flyAwayData.current.startTime = Date.now();
        }

        const elapsed = (Date.now() - flyAwayData.current.startTime) / 1000;
        const speed = 600; // Pixels per second

        // Update position based on the last angle
        xPos =
          flyAwayData.current.x +
          Math.cos(flyAwayData.current.angle) * speed * elapsed;
        yPos =
          flyAwayData.current.y +
          Math.sin(flyAwayData.current.angle) * speed * elapsed;
        angle = flyAwayData.current.angle;
      }
    }

    if (xPos !== undefined && yPos !== undefined) {
      ctx.save();
      // Round to whole pixels to prevent sub-pixel rendering jitter
      ctx.translate(Math.round(xPos), Math.round(yPos));
      ctx.rotate(angle);

      // Draw the airplane image if it's loaded
      if (airplaneImage.complete && airplaneImage.naturalWidth > 0) {
        const width = 116;
        const height = 90;
        // Position airplane so the tail tip is very close to the graph line endpoint
        // Move the airplane back (negative x) to bring tail tip to the line
        // Move upward (more negative y) to position it higher
        ctx.drawImage(airplaneImage, -25, -height / 2 - 0, width, height);
      }

      ctx.restore();
    }
  },
};

function SomeChart({ chartData, chartOptions, isLive }) {
  const flyAwayData = useRef(null);
  const containerRef = useRef(null);
  const cloudRef = useRef(null);
  const offsetRef = useRef(0);
  const lastTimeRef = useRef(null);
  const tileWidthRef = useRef(0);
  const decelerationDoneRef = useRef(false);
  const crashTimeRef = useRef(null);

  // Synchronize cloud loop with container height and grid speed
  useEffect(() => {
    const updateCloudVariables = () => {
      if (!containerRef.current) return;

      const height = containerRef.current.offsetHeight;
      // Image Aspect Ratio: 2465 / 631
      const tileWidth = height * (2465 / 631);
      tileWidthRef.current = tileWidth;

      containerRef.current.style.setProperty(
        "--cloud-tile-width",
        `${tileWidth}px`,
      );
    };

    updateCloudVariables();
    window.addEventListener("resize", updateCloudVariables);
    return () => window.removeEventListener("resize", updateCloudVariables);
  }, []);

  // JS-based animation loop for clouds to handle gradual deceleration
  useEffect(() => {
    let animationFrame;
    const DECELERATION_DURATION = 3500; // ms to come to a stop

    const animate = (time) => {
      if (!lastTimeRef.current) lastTimeRef.current = time;
      const deltaTime = time - lastTimeRef.current;
      lastTimeRef.current = time;

      if (isLive) {
        // Constant speed when live
        const speed = 58; // px per second
        offsetRef.current -= (speed * deltaTime) / 1000;
        decelerationDoneRef.current = false;
        crashTimeRef.current = null;
      } else {
        // Decelerate after flyaway
        if (!crashTimeRef.current) crashTimeRef.current = time;
        const elapsedSinceCrash = time - crashTimeRef.current;

        if (elapsedSinceCrash < DECELERATION_DURATION) {
          const t = elapsedSinceCrash / DECELERATION_DURATION;
          // Linear deceleration: speed goes from 58 to 0
          const currentSpeed = 58 * (1 - t);
          offsetRef.current -= (currentSpeed * deltaTime) / 1000;
        } else {
          decelerationDoneRef.current = true;
        }
      }

      // Loop offset back to 0 when it exceeds negative tileWidth
      if (tileWidthRef.current > 0) {
        if (offsetRef.current <= -tileWidthRef.current) {
          offsetRef.current += tileWidthRef.current;
        }
      }

      if (cloudRef.current) {
        cloudRef.current.style.transform = `translateX(${offsetRef.current}px)`;
      }

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animationFrame);
      lastTimeRef.current = null;
    };
  }, [isLive]);

  // Reset flyAwayData when game starts (before render to prevent flash of old position)
  if (isLive && flyAwayData.current && flyAwayData.current.startTime) {
    // Game just started, clear old crash position
    flyAwayData.current = null;
  }

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative chart-background ${isLive ? "moving-background" : ""}`}
    >
      <Chart
        type="line"
        data={chartData}
        options={{
          ...chartOptions,
          plugins: {
            ...chartOptions.plugins,
            airplane: {
              isLive: isLive,
              flyAwayData: flyAwayData,
            },
          },
        }}
        plugins={[airplanePlugin]}
      />

      {/* Cloud Container */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div
          ref={cloudRef}
          className="tiled-clouds-bg"
          style={{ transition: "none" }} // Ensure JS control is immediate
        />
      </div>
    </div>
  );
}

export default React.memo(SomeChart);
